/**
 * The Sticky-Centipede. Yellow, red-striped, far too many legs, and at home in
 * the water.
 *
 * The wiki's version is an ambush predator with four small dark eyes and
 * correspondingly poor sight, which lies motionless in a gap or under debris
 * for long stretches and coats itself in an adhesive it uses to hold prey. All
 * three of those are the behaviour here:
 *
 *  - It is effectively blind. `sightRange` is 9 m and it will not find anyone
 *    by looking. What it has instead is the longest hearing in the game, and
 *    the loudest thing a player can do in this building is walk through
 *    standing water (`TUNING.player.noiseWaterBonus`). This is the entity that
 *    turns the Poolrooms' own floor into the thing that gives you away.
 *  - It settles. Once it has a direction it holds still for a few seconds
 *    before it comes, which is what makes the approach read as an ambush
 *    rather than as a charge, and which is the beat the player can use.
 *  - It does not let go. `forget` is 0.07 a second — a twelfth of the hound's —
 *    so once it has you it keeps you for the best part of a quarter minute of
 *    silence. The adhesive, as a number.
 *
 * The counterplay is the water, and it is visible from across the room. In it
 * the thing runs at 5.9 m/s and beats a sprint; out of it, at 4.4 m/s, it loses
 * to one. Getting onto dry tile is the answer and it is the answer a player
 * works out for themselves the second time.
 */

import { TUNING } from '../../core/config';
import { hasLineOfSight } from '../../core/grid';
import { Cell } from '../../core/types';
import {
  driveAwareness,
  faceToward,
  holdStill,
  moveTo,
  setMode,
  sweepStep,
  takeHeardNoise,
  tryAttack,
  type Agent,
  type Behavior,
  type EntityWorld,
} from '../brain';
import { ENTITY_TUNING } from '../defs';
import { hearSignal, sightSignal } from '../perception';
import { VOICE } from '../voice';

const T = ENTITY_TUNING.centipede;

/** True when the cell under this body is standing water. */
function inWater(a: Agent, w: EntityWorld): boolean {
  const c = w.grid.worldToCell(a.state.pos.x, a.state.pos.z);
  return w.grid.at(c.cx, c.cy) === Cell.Water;
}

/**
 * How fast it may go from here. Water is worth `waterScale`; the first couple
 * of seconds of a lunge are worth `lungeScale` on top of that.
 */
function speedOf(a: Agent, w: EntityWorld, base: number): number {
  let v = base;
  if (inWater(a, w)) v *= T.waterScale;
  if (a.timer > 0) v *= T.lungeScale;
  return v;
}

/** Point its memory at a world position, allocating it once. */
function remember(a: Agent, x: number, z: number): void {
  const s = a.state;
  if (!s.lastKnown) s.lastKnown = { x, z };
  else {
    s.lastKnown.x = x;
    s.lastKnown.z = z;
  }
}

export const centipedeBehavior: Behavior = {
  kind: 'centipede',

  spawn(a) {
    // Long body, slow to come round. It commits to a direction.
    a.turnRate = 2.6;
    a.timer = 0;
    a.timer2 = 0;
    a.counter = 0;
  },

  think(a, w) {
    const s = a.state;
    a.timer = Math.max(0, a.timer - w.dt);

    const signal = Math.max(
      sightSignal(a.def, s, w.grid, w.player, w.playerLight),
      hearSignal(a.def, s, w.grid, w.player),
    );
    driveAwareness(a, w, signal, T.gain, T.forget);

    const heard = takeHeardNoise(a);
    if (heard) {
      remember(a, heard.x, heard.z);
      s.awareness = Math.min(1, s.awareness + a.heardLoudness * 0.55);
      a.searchTimer = TUNING.entities.searchDuration;
    }

    switch (s.mode) {
      case 'chase': {
        moveTo(a, w, s.lastKnown ?? w.player.pos, speedOf(a, w, a.def.chaseSpeed));

        // Water displaced by a great many legs. Loud in the wet, nearly silent
        // on dry floor, so the sound tells the player which of the two they are
        // dealing with.
        a.timer2 -= w.dt;
        if (a.timer2 <= 0) {
          const wet = inWater(a, w);
          a.timer2 = wet ? 0.9 : 1.6;
          w.audio.play(VOICE.centipedeSurge, { pos: s.pos, gain: wet ? 0.8 : 0.4, rate: 0.72 });
        }

        if (s.distToPlayer <= a.def.attackRange) {
          setMode(a, w, 'attack');
          break;
        }
        // Very hard to shake, but not impossible: `forget` finally wins after
        // about a quarter of a minute of the player making no noise at all.
        if (s.awareness < 0.06 && a.searchTimer <= 0) {
          s.lastKnown = null;
          setMode(a, w, 'wander');
        }
        break;
      }

      case 'attack': {
        faceToward(a, w.player.pos);
        holdStill(a);
        if (tryAttack(a, w)) {
          s.awareness = 1;
          remember(a, w.player.pos.x, w.player.pos.z);
        }
        w.dread(s, a.def.sanityDrain);
        if (s.distToPlayer > a.def.attackRange * 1.6) setMode(a, w, 'chase');
        break;
      }

      case 'stalk': {
        // Settled. It lies still with a direction in mind and waits, which is
        // the whole of what the wiki says it does with its time.
        holdStill(a);
        if (s.lastKnown) faceToward(a, s.lastKnown);
        a.timer2 -= w.dt;

        const clear = s.distToPlayer <= T.lungeRange && hasLineOfSight(w.grid, s.pos, w.player.pos);
        if (clear || a.timer2 <= 0) {
          // The lunge. `a.timer` is the burst window and is read by `speedOf`.
          if (clear) a.timer = T.lungeTime;
          w.audio.play(VOICE.centipedeSurge, { pos: s.pos, gain: 0.95, rate: 0.66 });
          setMode(a, w, 'chase');
        }
        if (s.awareness < 0.05 && a.searchTimer <= 0) {
          s.lastKnown = null;
          setMode(a, w, 'wander');
        }
        break;
      }

      case 'investigate': {
        if (!s.lastKnown) {
          setMode(a, w, 'wander');
          break;
        }
        moveTo(a, w, s.lastKnown, speedOf(a, w, a.def.walkSpeed * 1.8));

        if (s.awareness >= T.commitAwareness) {
          a.timer2 = a.rng.range(T.settleTime[0], T.settleTime[1]);
          setMode(a, w, 'stalk');
          break;
        }
        if (a.searchTimer <= 0) {
          s.lastKnown = null;
          s.awareness = 0;
          setMode(a, w, 'wander');
        }
        break;
      }

      default: {
        setMode(a, w, 'wander');
        sweepStep(a, w, speedOf(a, w, a.def.walkSpeed));
        if (s.awareness > 0.15 && s.lastKnown) setMode(a, w, 'investigate');
        break;
      }
    }
  },
};
